import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import type { OrganizationKind } from "@/shared/organizations/organization-link";
import { normalizeSearchTerm } from "./build-organizations-query";
import { ORGANIZATIONS_PAGE_SIZE, ORGANIZATIONS_SEARCH_DEBOUNCE_MS } from "./constants";
import { OrganizationsFilters } from "./OrganizationsFilters";
import { OrganizationsTable } from "./OrganizationsTable";
import { OrganizationsPagedResults } from "./OrganizationsPagedResults";
import { useOrganizationsList } from "./use-organizations-list";
import { useScopedPage } from "@/shared/hooks/use-scoped-page";

/**
 * Organizations directory for internal staff (ADR-0017). Mount it behind
 * `org.members.read`. Both ADR-0009 partitions are listed, test ones badged.
 * Filters live in component state (never the URL); the page resets with the
 * query's scope.
 */
export function OrganizationsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<OrganizationKind | null>(null);
  const debouncedSearch = useDebouncedValue(search, ORGANIZATIONS_SEARCH_DEBOUNCE_MS);
  // The page follows the query's scope (debounced search + kind): a new scope is
  // page 1 at once, so no request is made for the old page of the new search.
  const [page, setPage] = useScopedPage(`${normalizeSearchTerm(debouncedSearch)}|${kind ?? ""}`);
  const { data, isLoading, isFetching, isError, error, refetch } = useOrganizationsList({
    search: debouncedSearch,
    kind,
    page,
  });

  const filtered = debouncedSearch.trim().length > 0 || kind !== null;
  const items = data?.items ?? [];

  return (
    <div data-testid="organizations-screen" className="space-y-6">
      <header className="space-y-2">
        <p className="brand-eyebrow">{t("organizations.eyebrow")}</p>
        <h1 className="text-4xl">{t("organizations.title")}</h1>
        <p className="font-sans text-base font-normal text-muted-foreground">
          {t("organizations.subtitle")}
        </p>
        <p
          data-testid="organizations-names-note"
          className="flex items-center gap-2 font-sans text-sm text-muted-foreground"
        >
          <ShieldAlert className="h-4 w-4" aria-hidden="true" />
          {t("organizations.namesNote")}
        </p>
      </header>

      <OrganizationsFilters
        search={search}
        kind={kind}
        onSearchChange={setSearch}
        onKindChange={setKind}
        onReset={() => {
          setSearch("");
          setKind(null);
        }}
      />

      <OrganizationsPagedResults
        testIdPrefix="organizations"
        page={page}
        limit={data?.limit ?? ORGANIZATIONS_PAGE_SIZE}
        totalCount={data?.totalCount ?? 0}
        itemCount={items.length}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        error={error}
        onRetry={() => void refetch()}
        onPageChange={setPage}
        emptyBody={t(
          filtered ? "organizations.list.empty.filtered" : "organizations.list.empty.none",
        )}
        pastEndBody={t("organizations.list.empty.pastEnd")}
      >
        <OrganizationsTable items={items} />
      </OrganizationsPagedResults>
    </div>
  );
}
